import { useEffectOnce } from '@dni-react-hooks/use-effect-once'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { Dialog } from '@pets-ui/Dialog/Dialog'
import { Fade } from '@pets-ui/Fade/Fade'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import * as stylex from '@stylexjs/stylex'
import { useTranslation } from 'react-i18next'
import { usePopupTriggerService } from '../../hooks/usePopupTriggerService'
import { type PopupType } from '../../PopupContainer'
import { PopupActions } from '../PopupActions'
import { PopupContent } from '../PopupContent'
import { PopupHeader } from '../PopupHeader'

const baseStyle = stylex.create({
  checkboxContainer: {
    position: 'absolute',
    bottom: -28,
    left: 0,
  },
  backgroundHidden: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },
  backgroundHiddenCloseContent: {
    position: 'absolute',
    bottom: -38,
    left: -8,
  },
})
interface PopupDialogProps {
  onClose?: () => void
  open: boolean
  popupKey?: string
  onSubmitPopupImpression?: () => void
  onSubmitPopupClickNoShow?: () => void
  title?: string
  content?: string
  image?: string
  linkUrl?: string
  buttonText?: string
  popupType?: PopupType
  width?: number
  backgroundHidden?: boolean
  onPopupClickEvent?: () => void
}

export const PopupDialog = (props: PopupDialogProps) => {
  const {
    onClose,
    open,
    popupKey,
    onSubmitPopupImpression,
    onSubmitPopupClickNoShow,
    title,
    content,
    image,
    linkUrl,
    buttonText,
    popupType,
    width,
    backgroundHidden,
    onPopupClickEvent,
    ...rest
  } = props
  const { suppressToday } = usePopupTriggerService()
  const { t } = useTranslation()

  useEffectOnce(() => {
    if (open && onSubmitPopupImpression) {
      onSubmitPopupImpression()
    }
  })

  const handleDontShowToday = () => {
    onSubmitPopupClickNoShow?.()
    suppressToday(popupKey || '')
    onClose?.()
  }

  return (
    <Dialog
      open={open}
      xStyles={backgroundHidden
        ? {
          paper: baseStyle.backgroundHidden,
        }
        : undefined}
      {...rest}
    >
      <Fade in={open} duration={300}>
        <Box p="12px" pb={backgroundHidden ? 0 : '12px'}>
          <PopupHeader title={title} onClose={onClose} backgroundHidden={!!backgroundHidden} />
          <PopupContent
            backgroundHidden={!!backgroundHidden}
            popupType={popupType}
            content={content || ''}
            image={image || ''}
            linkUrl={linkUrl || ''}
            width={width}
            onClick={onClose}
            onPopupClickEvent={onPopupClickEvent}
          />
          <PopupActions
            title={buttonText || ''}
            link={linkUrl || ''}
            onClick={onClose}
            onPopupClickEvent={onPopupClickEvent}
          />
        </Box>
        {backgroundHidden && (
          <Button p="6.4px 9.6px" onClick={handleDontShowToday} variant="text" size="small">
            <Text color={colors.white}>{t('popup.dontShowToday', { defaultValue: '今日不再顯示' })}</Text>
          </Button>
        )}
        {!backgroundHidden && (
          <Button p="6.4px 9.6px" onClick={handleDontShowToday} variant="text" size="small" rootStyle={baseStyle.backgroundHiddenCloseContent}>
            <Text color={colors.white}>{t('popup.dontShowToday', { defaultValue: '今日不再顯示' })}</Text>
          </Button>
        )}
      </Fade>
    </Dialog>
  )
}
